import { IResearchedAd } from '@/api/research-jobs/ads/get-researched-ads-query'
import { Button } from '@/components/ui/button'
import { useResearchAdsFiltersContext } from '@/features/research/research-ads-filters/research-ads-filters-context'
import { useResearchAdsContext } from '@/features/research/research-ads/research-ads-context'
import { cn } from '@/utils/styles/cn'
import { Wand2, X } from 'lucide-react'
import { useMemo } from 'react'
import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import { IClassNameProps } from '@/utils/typings/class-name-props'
import { AccessControl } from '@/features/access-control/access-control'
import { SaveToBoardSplitButton } from '@/features/ad-boards/save-to-board-split-button'
import { AdPoolBadges } from '@/features/research/research-ads/legacy/ad-pool-manager'
import { ResearchAdCoreDetailRows } from '@/features/research/research-ads/research-ad-card/research-ad-detail-rows'
import { ResearchAdCardTextContent } from '@/features/research/research-ads/research-ad-card/research-ad-card-text-content'
import { getResearchAdTextContent } from '@/features/research/research-ads/research-ad-card/utils/research-ad-text-content'
import { useResearchAdActions } from '@/features/research/research-ads/research-ad-card/utils/use-research-ad-actions'

type ResearchAdCardMetricsProps = IClassNameProps & {
    ad: IResearchedAd
}

/** Copy + KPI rows — the ResourceCard body slot. */
export const ResearchAdCardMetrics = ({ ad, className }: ResearchAdCardMetricsProps) => {
    const {
        config: { access },
    } = useResearchAdsFiltersContext()
    const { config } = useResearchAdsContext()

    const { filtersStore } = useResearchAdsFiltersContext()
    const displayTimeframe = useStore(
        filtersStore,
        useShallow((state) => state.growthTimeframe)
    )

    const textContent = useMemo(() => getResearchAdTextContent(ad), [ad])
    const hasTextContent = textContent.length > 0

    return (
        <div className={cn('flex flex-1 flex-col', className)}>
            <div className="space-y-3 px-0 py-3">
                {hasTextContent && <ResearchAdCardTextContent textContent={textContent} />}

                <div className="px-3">
                    <ResearchAdCoreDetailRows
                        ad={ad}
                        displayTimeframe={displayTimeframe}
                        interactive={!config.readOnly}
                    />
                </div>

                <AccessControl rule={access.pool}>
                    <div className="px-3">
                        <AdPoolBadges pools={ad.pools} adUuid={ad.uuid} />
                    </div>
                </AccessControl>
            </div>
        </div>
    )
}

/**
 * Action row for the ResourceCard footer slot. The shell owns the top rule — this is CTAs only.
 * Save-to-board is the primary; Recreate is secondary. Selection mode disables rather than hides.
 */
export function ResearchAdCardFooter({
    ad,
    selectionMode = false,
}: {
    ad: IResearchedAd
    selectionMode?: boolean
}) {
    const { config } = useResearchAdsContext()
    const { clone, canClone } = useResearchAdActions(ad)

    if (config.readOnly) {
        if (config.onRecreate) {
            return (
                <div className="flex items-center">
                    <Button
                        variant="ds2-primary"
                        size="ds2-sm"
                        className="ml-auto shrink-0"
                        onClick={() => config.onRecreate?.(ad.uuid)}
                    >
                        <Wand2 className="h-3 w-3" />
                        Recreate
                    </Button>
                </div>
            )
        }
        if (config.buildRecreateHref) {
            return (
                <div className="flex items-center">
                    <Button
                        href={config.buildRecreateHref(ad.uuid)}
                        variant="ds2-primary"
                        size="ds2-sm"
                        className="ml-auto shrink-0"
                    >
                        <Wand2 className="h-3 w-3" />
                        Recreate
                    </Button>
                </div>
            )
        }
        return null
    }

    return (
        <div className="flex items-center gap-2">
            {canClone && (
                <Button
                    variant="ds2-secondary"
                    size="ds2-sm"
                    className="shrink-0"
                    onClick={clone}
                    disabled={selectionMode}
                >
                    <Wand2 className="h-3 w-3" />
                    Recreate
                </Button>
            )}
            {config.removeFromBoard && (
                <Button
                    variant="ds2-secondary"
                    size="ds2-sm"
                    className="ml-auto min-w-0"
                    onClick={() => config.removeFromBoard?.onRemove(ad.uuid)}
                    disabled={selectionMode}
                >
                    <X className="h-3 w-3 shrink-0" />
                    <span className="truncate">Remove from {config.removeFromBoard.boardName}</span>
                </Button>
            )}
            {config.allowSaveToBoard && (
                <SaveToBoardSplitButton
                    adUuid={ad.uuid}
                    // Optional access on purpose: web and API deploy independently, so
                    // a payload from an API without the boards field must not crash
                    // the grid — the button just mounts unsaved until the API catches up.
                    initialSavedBoard={ad.boards?.[0] ?? null}
                    disabled={selectionMode}
                    className="ml-auto min-w-0"
                />
            )}
        </div>
    )
}
